import { v7 } from 'uuid';
import { constantLive, LiveValue, NetworkError, ProblemError, type HttpClient, type Id, type Live, type Media, type MediaKind, type MediaService, type MediaUploadOptions, type MediaUrlState } from '@/model';
import { object } from './decode';
import type { ChunkHttpClient } from './http';
import type { AccountStore } from './store';
import { downloadMediaLink } from './content-links';

interface MediaEntry { live: LiveValue<MediaUrlState>; refs: number; abort?: AbortController; objectUrl?: string; generation: number }
const absentMedia = constantLive<MediaUrlState>({ status: 'error' });
export interface CoreMediaService extends MediaService { uploadAttachment(file: Blob, options: MediaUploadOptions, key: string): Promise<Media>; dispose(): void }
export function mediaKind(mime: string, kind: MediaKind): MediaKind { return ['text/html', 'application/xhtml+xml', 'image/svg+xml'].includes(mime.toLowerCase().split(';')[0] ?? '') ? 'file' : kind; }
export function createMediaService(db: AccountStore, http: HttpClient): CoreMediaService {
  const entries = new Map<string, MediaEntry & { value: Live<MediaUrlState> }>();
  let disposed = false;
  const uploadControllers = new Set<AbortController>();
  const cacheName = `hylo-web-media-${db.accountId}`;
  const bytes = async (mediaId: Id, variant: 'full' | 'thumb', signal?: AbortSignal): Promise<Blob> => {
    if (disposed) throw new NetworkError('This media session has ended');
    const key = `${mediaId}:${variant}`;
    const path = `/v1/media/${mediaId}${variant === 'thumb' ? '/thumb' : ''}`;
    const cacheKey = new URL(`/__hylo_media/${mediaId}/${variant}`, globalThis.location?.origin ?? 'http://localhost').toString();
    let cache: Cache | undefined;
    try { if (globalThis.caches) cache = await caches.open(cacheName); } catch { /* Storage can be denied or at quota; authenticated fetch remains available. */ }
    const cached = await cache?.match(cacheKey).catch(() => undefined);
    let blob: Blob;
    if (cached) blob = await cached.blob();
    else {
      blob = await http.blob(path, { signal });
      if (disposed || signal?.aborted) throw new NetworkError('This media session has ended');
      // Raster/vector bytes are used only as image resources; they are never injected as markup.
      try { await cache?.put(cacheKey, new Response(blob, { headers: { 'Content-Type': blob.type || 'application/octet-stream' } })); } catch { /* A full cache never prevents viewing the media. */ }
    }
    if (disposed || signal?.aborted) throw new NetworkError('This media session has ended');
    await db.mediaCache.put({ key, media_id: mediaId, variant, accessed_at: Date.now(), bytes: blob.size });
    return blob;
  };
  const release = (entry: MediaEntry) => {
    entry.generation++; entry.abort?.abort(); entry.abort = undefined;
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl); entry.objectUrl = undefined;
    entry.live.set({ status: 'loading' });
  };
  const load = (entry: MediaEntry, mediaId: Id, variant: 'full' | 'thumb') => {
    if (entry.abort || entry.objectUrl || disposed) return;
    const generation = ++entry.generation; const abort = new AbortController(); entry.abort = abort;
    void bytes(mediaId, variant, abort.signal).then(blob => {
      if (entry.generation !== generation || entry.refs === 0 || disposed) return;
      entry.objectUrl = URL.createObjectURL(blob); entry.live.set({ status: 'ready', url: entry.objectUrl });
    }).catch(() => { if (entry.generation === generation && entry.refs > 0 && !disposed) entry.live.set({ status: 'error' }); }).finally(() => { if (entry.generation === generation) entry.abort = undefined; });
  };
  const uploadInLifetime = async (file: Blob, options: MediaUploadOptions, key: string): Promise<Media> => {
    if (disposed) throw new NetworkError('This media session has ended');
    const kind = mediaKind(file.type, options.kind);
    const meta = kind === 'video' ? { width: options.meta?.width, height: options.meta?.height, duration_ms: options.meta?.duration_ms } : kind === 'audio' ? { duration_ms: options.meta?.duration_ms, waveform: options.meta?.waveform } : undefined;
    if (file.size <= 5 * 1024 * 1024) {
      const form = new FormData(); form.set('file', file, options.name ?? 'attachment'); form.set('kind', kind);
      if (meta) form.set('meta', JSON.stringify(meta));
      const response = object(await http.upload('/v1/media', form, options)); return object(response.media) as unknown as Media;
    }
    if (!('uploadChunk' in http) || typeof http.uploadChunk !== 'function') throw new NetworkError('Chunk transport is unavailable');
    const transport = http as ChunkHttpClient;
    let saved = await db.mediaUploads.get(key); let state: Record<string, unknown> | undefined;
    if (saved) {
      try { state = object(object(await http.get(`/v1/media/uploads/${saved.upload_id}`, { signal: options.signal })).upload); }
      catch (error) { if (!(error instanceof ProblemError) || error.status !== 404 && error.status !== 410) throw error; await db.mediaUploads.delete(key); saved = undefined; }
    }
    if (!saved) {
      const response = object(await http.post('/v1/media/uploads', { kind, mime: file.type || 'application/octet-stream', bytes: file.size, ...(options.name ? { file_name: options.name } : {}), ...(meta ? { meta } : {}) }, { signal: options.signal }));
      state = object(response.upload); saved = { key, upload_id: String(state.id) };
      if (disposed || options.signal?.aborted) throw new NetworkError('This media session has ended');
      await db.mediaUploads.put(saved);
    }
    let offset = Number(state?.offset ?? 0);
    if (!Number.isSafeInteger(offset) || offset < 0 || offset > file.size) throw new NetworkError('Invalid upload offset');
    while (offset < file.size) {
      const first = offset; const chunk = file.slice(first, Math.min(file.size, first + 2 * 1024 * 1024));
      try {
        const response = object(await transport.uploadChunk(`/v1/media/uploads/${saved.upload_id}`, chunk, first, file.size, { signal: options.signal, onProgress: (sent, _total) => options.onProgress?.(Math.min(file.size, first + sent), file.size) }));
        const next = Number(object(response.upload).offset);
        if (!Number.isSafeInteger(next) || next <= offset || next > file.size) throw new NetworkError('Invalid upload progress');
        offset = next;
      } catch (error) {
        if (error instanceof ProblemError && error.code === 'upload_offset_mismatch' && Number.isSafeInteger(error.problem.offset) && Number(error.problem.offset) >= 0 && Number(error.problem.offset) <= file.size && Number(error.problem.offset) !== offset) offset = Number(error.problem.offset);
        else throw error;
      }
      if (disposed || options.signal?.aborted) throw new NetworkError('This media session has ended');
      options.onProgress?.(offset, file.size);
    }
    const response = object(await http.post(`/v1/media/uploads/${saved.upload_id}/complete`, undefined, { signal: options.signal }));
    if (disposed || options.signal?.aborted) throw new NetworkError('This media session has ended');
    await db.mediaUploads.delete(key); return object(response.media) as unknown as Media;
  };
  const upload = async (file: Blob, options: MediaUploadOptions, key: string): Promise<Media> => {
    const controller = new AbortController(); uploadControllers.add(controller);
    const signal = options.signal ? AbortSignal.any([options.signal, controller.signal]) : controller.signal;
    try { return await uploadInLifetime(file, { ...options, signal }, key); }
    finally { uploadControllers.delete(controller); }
  };
  return {
    retry: async (id, variant) => { const entry = entries.get(`${id}:${variant}`); if (entry) { release(entry); if (entry.refs) load(entry, id, variant); } },
    upload: (file, options) => upload(file, options, v7()), uploadAttachment: upload,
    url(mediaId, variant) {
      if (!mediaId.trim()) return absentMedia;
      const key = `${mediaId}:${variant}`; const existing = entries.get(key); if (existing) return existing.value;
      const entry: MediaEntry & { value: Live<MediaUrlState> } = { live: new LiveValue<MediaUrlState>({ status: 'loading' }), refs: 0, generation: 0, value: undefined as unknown as Live<MediaUrlState> };
      entry.value = { get: entry.live.get, subscribe: listener => {
        entry.refs++; const unsubscribe = entry.live.subscribe(listener); load(entry, mediaId, variant);
        return () => { unsubscribe(); entry.refs--; if (entry.refs === 0) release(entry); };
      } };
      entries.set(key, entry); return entry.value;
    },
    async download(mediaId, filename) {
      if (!mediaId.trim()) return;
      if (disposed) throw new NetworkError('This media session has ended');
      const controller = new AbortController(); uploadControllers.add(controller);
      try { await downloadMediaLink(http, mediaId, filename, controller.signal); }
      finally { uploadControllers.delete(controller); }
    },
    dispose() { disposed = true; for (const controller of uploadControllers) controller.abort(); uploadControllers.clear(); for (const entry of entries.values()) release(entry); entries.clear(); },
  };
}

/** Switch account-scoped caches while preserving the public Lives' identity and clearing old URLs immediately. */
export function createMediaFacade(): MediaService & { set(service?: MediaService): void } {
  let current: MediaService | undefined;
  interface Entry { live: LiveValue<MediaUrlState>; value: Live<MediaUrlState>; refs: number; unsubscribe?: () => void; id: Id; variant: 'full' | 'thumb' }
  const entries = new Map<string, Entry>();
  const attach = (entry: Entry) => {
    entry.unsubscribe?.(); entry.unsubscribe = undefined;
    entry.live.set({ status: 'loading' });
    if (!current || entry.refs === 0) return;
    const source = current.url(entry.id, entry.variant); entry.live.set(source.get()); entry.unsubscribe = source.subscribe(() => entry.live.set(source.get()));
  };
  const service = () => { if (!current) throw new NetworkError('This media session has ended'); return current; };
  return {
    retry: async (id, variant) => { if (id.trim()) await service().retry?.(id, variant); },
    upload: (file, options) => service().upload(file, options), download: async (id, filename) => { if (id.trim()) await service().download(id, filename); },
    url(id, variant) {
      if (!id.trim()) return absentMedia;
      const key = `${id}:${variant}`; const held = entries.get(key); if (held) return held.value;
      const entry: Entry = { id, variant, live: new LiveValue<MediaUrlState>({ status: 'loading' }), refs: 0, value: undefined as unknown as Live<MediaUrlState> };
      entry.value = { get: entry.live.get, subscribe: listener => {
        entry.refs++; const stop = entry.live.subscribe(listener); if (entry.refs === 1) attach(entry);
        return () => { stop(); entry.refs--; if (entry.refs === 0) { entry.unsubscribe?.(); entry.unsubscribe = undefined; entry.live.set({ status: 'loading' }); } };
      } };
      entries.set(key, entry); return entry.value;
    },
    set(next) { if (next === current) return; current = next; for (const entry of entries.values()) attach(entry); },
  };
}
